import { useState } from 'react'
import { useAuthStore } from '../hooks/useAuth'
import {
  Award, Download, Share2, CheckCircle, Lock, Star,
  Calendar, Clock, Filter, Search
} from 'lucide-react'
import toast from 'react-hot-toast'

interface Certificate {
  id: string
  title: string
  courseName: string
  studentName: string
  issueDate: string
  expiryDate: string
  score: number
  grade: string
  verificationCode: string
  status: 'earned' | 'in_progress' | 'locked'
  thumbnail: string
}

export default function Certificates() {
  const { user } = useAuthStore()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')

  const certificates: Certificate[] = [
    {
      id: 'cert1', title: 'Advanced Mathematics Certificate',
      courseName: 'Advanced Mathematics',
      studentName: user?.name || 'Student',
      issueDate: '2026-04-15', expiryDate: '2028-04-15',
      score: 92, grade: 'A',
      verificationCode: 'MAZ-2026-AM-9247',
      status: 'earned',
      thumbnail: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400'
    },
    {
      id: 'cert2', title: 'Python Programming Certificate',
      courseName: 'Python Programming Masterclass',
      studentName: user?.name || 'Student',
      issueDate: '2026-03-20', expiryDate: '2028-03-20',
      score: 88, grade: 'B+',
      verificationCode: 'MAZ-2026-PY-8832',
      status: 'earned',
      thumbnail: 'https://images.unsplash.com/photo-1526379095098-d400fd0bf935?w=400'
    },
    {
      id: 'cert3', title: 'Physics Fundamentals Certificate',
      courseName: 'Physics Fundamentals',
      studentName: user?.name || 'Student',
      issueDate: '', expiryDate: '',
      score: 0, grade: '',
      verificationCode: '',
      status: 'in_progress',
      thumbnail: 'https://images.unsplash.com/photo-1636466497213-4320c0c4c186?w=400'
    },
    {
      id: 'cert4', title: 'Machine Learning Certificate',
      courseName: 'Machine Learning & AI',
      studentName: user?.name || 'Student',
      issueDate: '', expiryDate: '',
      score: 0, grade: '',
      verificationCode: '',
      status: 'locked',
      thumbnail: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400'
    },
  ]

  const filtered = certificates.filter(c => {
    const matchesSearch = c.title.toLowerCase().includes(search.toLowerCase()) ||
                         c.courseName.toLowerCase().includes(search.toLowerCase())
    const matchesFilter = filter === 'all' || c.status === filter
    return matchesSearch && matchesFilter
  })

  const downloadCertificate = (cert: Certificate) => {
    // In production: generate PDF
    toast.success('Certificate download started!')
  }

  const verifyCertificate = (code: string) => {
    toast.success(`Certificate verified: ${code}`)
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Certificates</h1>
          <p className="text-slate-500 mt-1">Your achievements and credentials</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-3xl font-bold text-success">{certificates.filter(c => c.status === 'earned').length}</p>
          <p className="text-sm text-slate-500 mt-1">Earned</p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-3xl font-bold text-warning">{certificates.filter(c => c.status === 'in_progress').length}</p>
          <p className="text-sm text-slate-500 mt-1">In Progress</p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-3xl font-bold text-slate-500">{certificates.filter(c => c.status === 'locked').length}</p>
          <p className="text-sm text-slate-500 mt-1">Locked</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search certificates..."
            className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'earned', 'in_progress', 'locked'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f ? 'bg-primary text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {f === 'in_progress' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Certificates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filtered.map(cert => (
          <div key={cert.id} className={`glass rounded-xl overflow-hidden ${
            cert.status === 'locked' ? 'opacity-60' : ''
          }`}>
            <div className="relative h-40">
              <img src={cert.thumbnail} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent" />
              <div className="absolute top-3 right-3">
                {cert.status === 'earned' && (
                  <span className="px-2 py-1 bg-success/20 text-success rounded text-xs font-medium">
                    <CheckCircle className="w-3 h-3 inline mr-1" /> Earned
                  </span>
                )}
                {cert.status === 'in_progress' && (
                  <span className="px-2 py-1 bg-warning/20 text-warning rounded text-xs font-medium">
                    In Progress
                  </span>
                )}
                {cert.status === 'locked' && (
                  <span className="px-2 py-1 bg-slate-700 text-slate-400 rounded text-xs font-medium">
                    <Lock className="w-3 h-3 inline mr-1" /> Locked
                  </span>
                )}
              </div>
            </div>
            <div className="p-5">
              <h3 className="font-semibold text-lg mb-1">{cert.title}</h3>
              <p className="text-sm text-slate-500 mb-3">{cert.courseName}</p>

              {cert.status === 'earned' && (
                <>
                  <div className="flex items-center gap-4 mb-3 text-sm">
                    <span className="flex items-center gap-1 text-slate-500">
                      <Calendar className="w-3 h-3" /> {cert.issueDate}
                    </span>
                    <span className="flex items-center gap-1 text-slate-500">
                      <Star className="w-3 h-3 text-amber-400" /> {cert.grade}
                    </span>
                    <span className="flex items-center gap-1 text-slate-500">
                      <Award className="w-3 h-3" /> {cert.score}%
                    </span>
                  </div>
                  <div className="p-2 bg-slate-800/50 rounded-lg mb-3">
                    <p className="text-xs text-slate-500">Verification Code</p>
                    <code className="text-sm font-mono text-primary">{cert.verificationCode}</code>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => downloadCertificate(cert)}
                      className="flex-1 flex items-center justify-center gap-2 py-2 bg-primary hover:bg-primary-dark rounded-lg text-sm font-medium transition-colors"
                    >
                      <Download className="w-4 h-4" /> Download
                    </button>
                    <button
                      onClick={() => verifyCertificate(cert.verificationCode)}
                      className="flex-1 flex items-center justify-center gap-2 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm transition-colors"
                    >
                      <Share2 className="w-4 h-4" /> Verify
                    </button>
                  </div>
                </>
              )}

              {cert.status === 'in_progress' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Progress</span>
                    <span>65%</span>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-warning rounded-full" style={{ width: '65%' }} />
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Complete the course to earn this certificate</p>
                </div>
              )}

              {cert.status === 'locked' && (
                <div className="text-center py-4">
                  <Lock className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Enroll in the course to unlock this certificate</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
